import { atom } from 'jotai'
import { atomFamily, selectAtom } from 'jotai/utils'
import isEqual from 'lodash/isEqual'

import { isInstructor } from '../../share/utility'
import AuthStateAtom from '../auth/AtomAuth'

const createUsersState = function () {
  const authDataAtom = AuthStateAtom.authData

  // A quick way to get auth user, don't use it as user since the information may be out of date
  const authUserAtom = AuthStateAtom.authUser

  // 当前用户相关的状态（如：我的教练课程列表）
  const userAtom = {
    myInstructorSessions: atom([]),
    myInstructorSessionsLoading: atom(false),
  }

  const usersAtom = atom({})

  const usersAtom_OLD = atom({}, (get, set, update) => {
    const prev = get(usersAtom)

    // if (update == null || (typeof update === 'object' && update !== null && Object.keys(update).length === 0)) {
    //   set(usersAtom, {})
    //   return
    // }

    // if update is a function, call it with prev as argument
    if (typeof update === 'function') {
      console.log('update is a function, call it with prev as argument')
      const updatedContent = update(prev)
      // This could compare hundrans user but not very efficient, but should be fine for now
      // Another way is rely on update function to do different comparison logic
      // In this way, I have to specify how it should be used
      console.log('updatedContent', updatedContent)
      console.log('prev', prev)
      if (updatedContent && isEqual(prev, updatedContent)) {
        return
      }

      set(usersAtom, updatedContent)
      return
    }

    if (Array.isArray(update)) {
      console.log('update is an array, loop through it and check if there is a change')
      let updateObject = {}
      let isChanged = false
      update.forEach((user) => {
        if (user?.resourceId != null) {
          let prev_user = prev[user.resourceId]
          if (!isEqual(prev_user, user)) {
            isChanged = true
            updateObject[user.resourceId] = user
          }
        }
      })

      if (isChanged) {
        set(usersAtom, { ...prev, ...updateObject })
      }
      return
    }

    // 3. update is a object, just merge it with prev
    // Make sure update is an object

    console.log('update is an object, loop through it and check if there is a change')
    if (typeof update !== 'object' || update === null || Array.isArray(update)) {
      return
    }
    let updateObject = {}
    let isChanged = false
    // loops through the update object and check if there is a change
    Object.keys(update).forEach((key) => {
      if (!isEqual(prev[key], update[key])) {
        updateObject[key] = update[key]
        isChanged = true
      }
    })
    if (isChanged) {
      set(usersAtom, { ...prev, ...updateObject })
    }
  })

  // 正在加载的用户 ID 集合
  const loadingUsersAtom = atom(new Set())

  const usersByIdsAtomFamily = atomFamily((ids) =>
    selectAtom(
      usersAtom,
      (allUsers) =>
        ids
          .map((id) => allUsers[id])
          .filter(Boolean)
          .sort((a, b) => a.resourceId.localeCompare(b.resourceId)),
      // Add a comparator for select atom, make sure lengths equals and item equals
      (a, b) => a.length === b.length && a.every((item, index) => item === b[index]),
    ),
  )

  // 用户数据 atom 家族（根据 userId 动态创建 atom）
  const userAtomFamily = atomFamily((userId) =>
    atom(
      () => {
        return selectAtom(
          usersAtom,
          (allUsers) => allUsers[userId] || null,
          (a, b) => isEqual(a, b),
        )
      },
      (get, set, update) => {
        const prevUsers = get(usersAtom)
        const prevUser = prevUsers[userId] || {}
        const newUser = { ...prevUser, ...update }

        // Make sure there is a change
        if (isEqual(prevUser, newUser)) {
          return
        }

        set(usersAtom, {
          ...prevUsers,
          [userId]: newUser,
        })
      },
    ),
  )

  const userReadonlyAtom = atomFamily((userId) => atom((get) => get(selectAtom(usersAtom, (users) => users[userId] || null, isEqual))))

  // 用户加载状态 atom 家族（根据 userId 动态创建加载状态 atom）
  const userLoadingAtomFamily = atomFamily((userId) =>
    atom(
      (get) => {
        const loadingUsers = get(loadingUsersAtom)
        return loadingUsers.has(userId)
      },
      (get, set, update) => {
        const prevLoadingUsers = get(loadingUsersAtom)
        const nextLoadingUsers = new Set(prevLoadingUsers)
        if (update) {
          nextLoadingUsers.add(userId)
        } else {
          nextLoadingUsers.delete(userId)
        }
        set(loadingUsersAtom, nextLoadingUsers)
      },
    ),
  )

  const currentUserAtom = atom((get) => {
    const authUser = get(authUserAtom)
    const resourceId = authUser?.resourceId
    if (resourceId) {
      return get(userReadonlyAtom(resourceId))
    }
    return null
  })

  // 教练列表
  const instructorsAtom = atom((get) => {
    const users = get(usersAtom)
    return Object.values(users)
      .filter(isInstructor)
      .sort((a, b) => a.name.localeCompare(b.name))
  })

  // 缓存时间记录（以 userId 或 key 为键的对象）
  const userLastUpdateTimeAtom = atom({})

  return {
    authData: authDataAtom,
    authUser: authUserAtom,
    currentUser: currentUserAtom,

    users: usersAtom,
    loadingUsers: loadingUsersAtom,

    userAtomFamily,
    userReadonlyAtom,
    userLoadingAtomFamily,
    usersByIdsAtomFamily,

    instructors: instructorsAtom,
    userLastUpdateTime: userLastUpdateTimeAtom,

    fetchAllUsersFinished: atom(false),
    fetchAllUsersLoading: atom(false),
    fetchAllUsersError: atom(null),

    fetchAllInstructorsFinished: atom(false),
    fetchAllInstructorsLoading: atom(false),
    fetchAllInstructorsError: atom(null),
  }
}

const usersStateAtom = createUsersState()

export default usersStateAtom
export const authDataAtom = usersStateAtom.authData
export const authUserAtom = usersStateAtom.authUser
export const currentUserAtom = usersStateAtom.currentUser
export const usersAtom = usersStateAtom.users
export const loadingUsersAtom = usersStateAtom.loadingUsers
export const userAtomFamily = usersStateAtom.userAtomFamily
export const userReadonlyAtom = usersStateAtom.userReadonlyAtom
export const userLoadingAtomFamily = usersStateAtom.userLoadingAtomFamily
export const usersByIdsAtomFamily = usersStateAtom.usersByIdsAtomFamily

export const instructorsAtom = usersStateAtom.instructors
export const userLastUpdateTimeAtom = usersStateAtom.userLastUpdateTime
export const fetchAllUsersFinishedAtom = usersStateAtom.fetchAllUsersFinished
export const fetchAllUsersLoadingAtom = usersStateAtom.fetchAllUsersLoading
export const fetchAllUsersErrorAtom = usersStateAtom.fetchAllUsersError
export const fetchAllInstructorsFinishedAtom = usersStateAtom.fetchAllInstructorsFinished
export const fetchAllInstructorsLoadingAtom = usersStateAtom.fetchAllInstructorsLoading
export const fetchAllInstructorsErrorAtom = usersStateAtom.fetchAllInstructorsError
